import { useContext, useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import useLoyaltyPointsEarnCalculator from './useLoyaltyPointsEarnCalculator'
import { calculateTierProgressionEligibility } from 'loyalty/lib/calculateTierProgressionEligibility'
import { generateLoyaltyPointsCalculatorPropertyOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import getLoyaltyTierThresholds from 'loyalty/selectors/getLoyaltyTierThresholds'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import moment from 'moment'
import { getTierRank } from 'loyalty/lib/tierUtils'

interface Params {
  property: App.Property.Property | undefined
  rates: Array<App.EnrichedPropertyRate>
  nights: number
  checkoutDate?: string
  isUltraLux?: boolean
  skip?: boolean
}

type PropertyRateTierProgressionResult =
  | {
    showTierProgressionBanner: true
    /** The lowest tier that any rate can unlock */
    lowestUnlockableTier: App.LoyaltyTier
    lowestUnlockableTierName: string
    /** The highest tier that any rate can reach */
    highestUnlockableTier: App.LoyaltyTier
    highestUnlockableTierName: string
    /** True if lowest !== highest (show "fast-track" messaging) */
    hasMultipleTierJumps: boolean
    isEligible: true
    isLoading: false
    statusReviewDate: string | undefined
    eligibleRate: App.EnrichedPropertyRate
  }
  | {
    showTierProgressionBanner: false
    lowestUnlockableTier: undefined
    lowestUnlockableTierName: undefined
    highestUnlockableTier: undefined
    highestUnlockableTierName: undefined
    hasMultipleTierJumps: false
    isEligible: boolean
    isLoading: boolean
    statusReviewDate: string | undefined
    eligibleRate: undefined
  }

function usePropertyRateTierProgression(params: Params): PropertyRateTierProgressionResult {
  const { property, rates, nights, checkoutDate, isUltraLux = false, skip = false } = params
  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const loyaltyAccountLoading = useAppSelector(
    state => state.loyalty.account.fetching || state.loyalty.account.initial,
  )
  const pendingPoints = useAppSelector(state => state.loyalty.accountPendingPoints.data)
  const tierThresholds = useAppSelector(getLoyaltyTierThresholds)

  const calculationRequests = useMemo(() => {
    if (skip || loyaltyAccountLoading || !property || rates.length === 0 || nights <= 0) {
      return []
    }

    const propertyCountryCode = property.location.countryCode
    const propertyState = property.location.region

    return rates.map(rate =>
      generateLoyaltyPointsCalculatorPropertyOptions(rate, {
        propertyCountryCode,
        propertyState,
        currentRegionCode,
        isUltraLux,
        nights,
      }),
    )
  }, [skip, loyaltyAccountLoading, property, rates, currentRegionCode, isUltraLux, nights])

  const { pointsCalculations } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: skip || calculationRequests.length === 0,
    useBatch: true,
  })

  const result = useMemo((): PropertyRateTierProgressionResult => {
    const anyLoading = pointsCalculations.some(calc => calc?.initial || calc?.fetching)
    const isLoading = loyaltyAccountLoading || anyLoading || !tierThresholds

    if (skip || isLoading || !loyaltyAccount || rates.length === 0 || !tierThresholds) {
      return {
        showTierProgressionBanner: false,
        lowestUnlockableTier: undefined,
        lowestUnlockableTierName: undefined,
        highestUnlockableTier: undefined,
        highestUnlockableTierName: undefined,
        hasMultipleTierJumps: false,
        isEligible: false,
        isLoading: !skip && isLoading,
        statusReviewDate: loyaltyAccount?.statusReviewDate,
        eligibleRate: undefined,
      }
    }

    const pointsEarnDate = checkoutDate
      ? moment(checkoutDate).add(1, 'day').format('YYYY-MM-DD')
      : undefined

    // Track across ALL rates:
    // - globalLowestTier: the lowest tier any rate can unlock
    // - globalHighestTier: the highest tier any rate can reach
    let globalLowestTier: App.LoyaltyTier | undefined
    let globalHighestTier: App.LoyaltyTier | undefined
    let bestRate: App.EnrichedPropertyRate | undefined

    for (let i = 0; i < rates.length; i++) {
      const rate = rates[i]
      const calculation = pointsCalculations[i]

      if (!calculation?.data) continue

      const baseStatusCredits = calculation.data.statusCredits ?? 0
      const bonusStatusCredits = calculation.data.bonusStatusCredits ?? 0
      const totalStatusCredits = baseStatusCredits + bonusStatusCredits

      const eligibility = calculateTierProgressionEligibility({
        currentTier: loyaltyAccount.tier,
        currentStatusCredits: loyaltyAccount.statusCredits ?? 0,
        currentNights: loyaltyAccount.numberOfNights ?? 0,
        statusCredits: totalStatusCredits,
        nights,
        pointsEarnDate,
        statusReviewDate: loyaltyAccount.statusReviewDate,
        pendingPoints: pendingPoints ?? [],
        tierThresholds,
      })

      if (eligibility.isEligible) {
        // Track the LOWEST unlockable tier across all rates
        const currentLowestRank = getTierRank(globalLowestTier)
        const newLowestRank = getTierRank(eligibility.lowestUnlockableTier)
        if (currentLowestRank === -1 || newLowestRank < currentLowestRank) {
          globalLowestTier = eligibility.lowestUnlockableTier
        }

        // Track the HIGHEST unlockable tier across all rates
        const currentHighestRank = getTierRank(globalHighestTier)
        const newHighestRank = getTierRank(eligibility.highestUnlockableTier)
        if (newHighestRank > currentHighestRank) {
          globalHighestTier = eligibility.highestUnlockableTier
          bestRate = rate
        }
      }
    }

    // Return combined result if we found any eligible rates
    if (globalLowestTier && globalHighestTier && bestRate) {
      const hasMultipleTierJumps = globalLowestTier !== globalHighestTier

      return {
        showTierProgressionBanner: true,
        lowestUnlockableTier: globalLowestTier,
        lowestUnlockableTierName: getLoyaltyTierDisplayName(globalLowestTier),
        highestUnlockableTier: globalHighestTier,
        highestUnlockableTierName: getLoyaltyTierDisplayName(globalHighestTier),
        hasMultipleTierJumps,
        isEligible: true,
        isLoading: false,
        statusReviewDate: loyaltyAccount.statusReviewDate,
        eligibleRate: bestRate,
      }
    }

    return {
      showTierProgressionBanner: false,
      lowestUnlockableTier: undefined,
      lowestUnlockableTierName: undefined,
      highestUnlockableTier: undefined,
      highestUnlockableTierName: undefined,
      hasMultipleTierJumps: false,
      isEligible: false,
      isLoading: false,
      statusReviewDate: loyaltyAccount.statusReviewDate,
      eligibleRate: undefined,
    }
  }, [skip, loyaltyAccountLoading, loyaltyAccount, rates, pointsCalculations, nights, checkoutDate, pendingPoints, tierThresholds])

  return result
}

export default usePropertyRateTierProgression
